import { z } from 'zod'
import {
  computeCostScenario,
  COST_SCENARIO_VERSION,
  DEFAULT_SALES_TAX_RATE as ENGINE_SALES_TAX_RATE,
  div,
  FOOD_ENGINE_VERSION,
  fromExact,
  scaleScenarios,
  toExact,
  type ScenarioIssue,
  type SourceType,
} from '@repo/food-engine'
import { priceEvidenceSchema, type Market } from './schemas/simulation'
import { SOURCE_TAGS } from './source-type'

/**
 * 원가 시나리오 (C46) 입력 검증. 계산은 공통 엔진(packages/food-engine)이 맡고, 여기에는 저장소 규칙대로
 * 입력 검증(zod) 스키마만 둔다. 출처 설명은 엔진의 cost-scenario.ts·pricing.ts 에 있다.
 */
export { COST_SCENARIO_VERSION, exactToNumber } from '@repo/food-engine'

/** 시장별 판매세 기본값 (엔진 값과 같음, 시장 타입만 shared 의 Market 으로 묶는다) */
export const DEFAULT_SALES_TAX_RATE: Record<Market, number | null> = ENGINE_SALES_TAX_RATE

const finite = z.number().finite()
const evidence = z.enum(['planned', 'measured', 'unknown'])
const taxBasis = z.enum(['exclusive', 'inclusive', 'unknown'])
const quantity = z
  .object({ amount: finite.nonnegative().nullable(), unit: z.string().max(20) })
  .strict()
const money = z
  .object({
    amount: finite.nonnegative().nullable(),
    currency: z.enum(['KRW', 'USD']).nullable(),
    taxBasis,
    evidence: priceEvidenceSchema.optional(),
  })
  .strict()
const amountLine = z
  .object({
    id: z.string().min(1).max(60),
    basis: z.enum(['per_batch', 'per_physical_unit', 'per_sales_pack']),
    amount: money,
  })
  .strict()
const sellingLine = z.union([
  z
    .object({
      id: z.string().min(1).max(60),
      type: z.literal('amount'),
      basis: z.enum(['per_order', 'per_physical_unit', 'per_sales_pack']),
      amount: money,
    })
    .strict(),
  z
    .object({
      id: z.string().min(1).max(60),
      type: z.literal('rate'),
      basis: z.literal('merchandise_revenue'),
      fraction: finite.nonnegative().nullable(),
      evidence: priceEvidenceSchema.optional(),
    })
    .strict(),
])
const count = finite.int().nonnegative().safe()
export const costScenarioInputSchema = z
  .object({
    currency: z.enum(['KRW', 'USD']),
    /** 모든 금액이 따르는 세금 기준. 금액마다 같아야 하고, 모르면 금액 결과를 보류한다 */
    taxBasis,
    inputs: z
      .array(z.object({ qty: finite.positive(), unit: z.string().max(20) }).strict())
      .min(1)
      .max(25),
    inputEvidence: evidence,
    /** 완성량을 직접 주거나, 투입량에 남는 비율(수율)을 곱한다. 둘 중 하나만 */
    output: z.union([
      z
        .object({ kind: z.literal('finished_quantity'), quantity: quantity.nullable(), evidence })
        .strict(),
      z
        .object({
          kind: z.literal('retained_fraction'),
          fraction: finite.min(0).max(1).nullable(),
          evidence,
        })
        .strict(),
    ]),
    /** 낱개 하나에 담는 양 */
    fillQuantity: quantity.nullable(),
    /** 팔 낱개 수. 'all_filled' 는 채운 낱개 전부 (C46) */
    saleablePhysicalUnits: z
      .object({ count: z.union([count, z.literal('all_filled')]).nullable(), evidence })
      .strict(),
    physicalUnitsPerSalesPack: finite.int().positive().safe().nullable(),
    /** 한 주문의 판매 팩 수. 'all_complete_packs' 는 다 채운 팩 전부 (C46) */
    orderSalesPacks: z
      .union([finite.int().positive().safe(), z.literal('all_complete_packs')])
      .nullable(),
    manufacturingLines: z.array(amountLine).max(50).nullable(),
    sellingLines: z.array(sellingLine).max(50).nullable(),
    costExclusions: z
      .array(
        z
          .object({
            id: z.string().min(1).max(60),
            scope: z.enum(['manufacturing', 'selling']),
            status: z.enum(['not_applicable', 'outside_selected_scope', 'unknown']),
          })
          .strict(),
      )
      .max(50),
    pricePerSalesPack: money.nullable(),
    /** 세금 별도 판매가에 얹어 보일 세율 (0.1 = 10%). 마진·남는 돈 계산에는 들어가지 않는다 (C46) */
    salesTaxRate: finite.min(0).max(1).nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    for (const key of ['manufacturingLines', 'sellingLines'] as const) {
      const ids = value[key]?.map((line) => line.id) ?? []
      if (new Set(ids).size !== ids.length)
        ctx.addIssue({ code: 'custom', path: [key], message: 'Duplicate scenario cost line' })
    }
    const keys = value.costExclusions.map((item) => `${item.scope}:${item.id}`)
    const included = [
      ...(value.manufacturingLines ?? []).map((line) => `manufacturing:${line.id}`),
      ...(value.sellingLines ?? []).map((line) => `selling:${line.id}`),
    ]
    if (new Set(keys).size !== keys.length || keys.some((key) => included.includes(key)))
      ctx.addIssue({
        code: 'custom',
        path: ['costExclusions'],
        message: 'Contradictory scenario cost scope',
      })
  })
export type CostScenarioInput = z.infer<typeof costScenarioInputSchema>

export type { ExactValue, ScenarioIssue } from '@repo/food-engine'

/**
 * 시나리오 계산. 입력이 스키마에 안 맞으면 status 'invalid'. 계산은 공통 엔진(@repo/food-engine computeCostScenario)이 한다:
 * 값이 모자라면 'incomplete'(되는 칸만 채움), 다 되면 'complete'.
 */
export function calculateCostScenario(raw: unknown) {
  const parsed = costScenarioInputSchema.safeParse(raw)
  if (!parsed.success)
    return {
      status: 'invalid' as const,
      version: COST_SCENARIO_VERSION,
      issues: [{ code: 'INVALID_INPUT', field: 'input' }],
      result: null,
    }
  return computeCostScenario(parsed.data)
}

// ── 규모별 손익 사다리 (대표 Top5 #4, 2026-10-30) ─────────────────────────────────────────────

/**
 * 규모별 손익 사다리. 원가 시나리오가 낸 낱개 원가(또는 공장 견적 단가)에 MOQ·초기 지출·생산 1회 비용을 더해
 * 100 / 500 / 1,000 / MOQ 개를 팔 때의 낱개 원가·남는 돈·회수 판매량을 나란히 낸다. 계산은 공통 엔진의 scaleScenarios 가 한다
 * (MOQ 아래면 MOQ 만큼 만든 비용을 싣고, 초기 지출은 그 수량에 나눠 싣는다). 여기서는 입력 검증과 '모르는 값'의 범위만 정한다.
 * - 값마다 근거 꼬리표(source-type.ts)를 받는다. 금액이 null 이거나 미확인이면 0 으로 보지 않는다:
 *   낱개 원가·판매가를 모르면 사다리 전체를 보류하고, 초기 지출 일부를 모르면 아는 지출만 싣되 회수 판매량은 보류한다.
 * - 판매 수수료를 모르면 빼고 계산하고 sellingFeesIncluded 로 알린다. 고정비(임대료·인건비)는 늘 빠진다 — 순이익이 아니다.
 * - 재료비는 수량에 비례한다고 본다(선형 환산). 공정 시간·온도·혼합 조건의 확대는 검증하지 않는다 — 화면에 그렇게 적는다.
 */
export const SCALE_LADDER_VERSION = `scale-ladder-v2+${FOOD_ENGINE_VERSION}` as const
/** 기본 규모 칸 (대표 지시: 100 / 500 / 1,000 / MOQ). MOQ 는 알 때만 더한다 */
export const SCALE_LADDER_QUANTITIES = [100, 500, 1000] as const
/** 초기 지출 항목 (한 번만 드는 돈): 시제품비 · 포장재 최소 주문 · 개발비 */
export const SCALE_OUTLAY_IDS = ['prototype', 'packaging_minimum', 'development'] as const
export type ScaleOutlayId = (typeof SCALE_OUTLAY_IDS)[number]

const sourceTag = z.enum(SOURCE_TAGS)
const sourcedAmount = z
  .object({ amount: finite.nonnegative().nullable(), source: sourceTag })
  .strict()
export const scaleLadderInputSchema = z
  .object({
    currency: z.enum(['KRW', 'USD']),
    /** 낱개 하나의 변동 제조 원가 (세금 별도): 재료·포장·노무. 시나리오의 낱개 원가 또는 공장 견적 단가 */
    unitCost: sourcedAmount,
    /** 낱개 판매가 (세금 별도) */
    unitPrice: sourcedAmount,
    /** 판매 채널 수수료 (매출 비율, 0.1 = 10%). null 이면 수수료를 빼고 계산한다 */
    channelFeeRate: z
      .object({ fraction: finite.min(0).max(1).nullable(), source: sourceTag })
      .strict(),
    /** 첫 생산 1회에 드는 비용 (견적의 배치당 항목). 초기 지출과 함께 싣는다 */
    runCosts: z
      .array(z.object({ id: z.string().min(1).max(60), amount: sourcedAmount }).strict())
      .max(20),
    /** 초기 지출 (한 번만). 모르는 항목은 amount null — 회수 판매량을 보류한다. 해당 없으면 0 을 넣는다 */
    outlays: z
      .array(z.object({ id: z.string().min(1).max(60), amount: sourcedAmount }).strict())
      .max(20),
    moq: z.object({ count: count.nullable(), source: sourceTag }).strict(),
    /** 비교할 판매 수량 (낱개). MOQ 칸은 계산이 더한다 */
    quantities: z.array(finite.int().positive().safe().max(10_000_000)).min(1).max(8),
  })
  .strict()
export type ScaleLadderInput = z.infer<typeof scaleLadderInputSchema>

type Sourced = z.infer<typeof sourcedAmount>
/** 엔진에 넘길 값. 미확인 꼬리표는 숫자가 있어도 모르는 값이다 */
const engineAmount = (value: Sourced) =>
  value.amount === null || value.source === 'unconfirmed'
    ? { amount: null }
    : { amount: value.amount, source: value.source as SourceType }

/** 사다리 계산. 입력이 스키마에 안 맞으면 invalid, 모르는 값이 있으면 incomplete(되는 칸만), 다 되면 complete */
export function calculateScaleLadder(raw: unknown) {
  const parsed = scaleLadderInputSchema.safeParse(raw)
  if (!parsed.success)
    return {
      status: 'invalid' as const,
      version: SCALE_LADDER_VERSION,
      issues: [{ code: 'INVALID_INPUT', field: 'input' }],
      result: null,
    }
  const input = parsed.data
  const issues: ScenarioIssue[] = []
  const known = [...input.outlays, ...input.runCosts].filter((line) => {
    const ok = line.amount.amount !== null && line.amount.source !== 'unconfirmed'
    if (!ok) issues.push({ code: 'OUTLAY_MISSING', field: `outlays.${line.id}` })
    return ok
  })
  const initialSpend = known.reduce((total, line) => total + line.amount.amount!, 0)
  const moq = input.moq.source === 'unconfirmed' ? null : input.moq.count
  if (moq === null) issues.push({ code: 'MOQ_MISSING', field: 'moq' })
  const feeKnown =
    input.channelFeeRate.fraction !== null && input.channelFeeRate.source !== 'unconfirmed'
  const quantities = [...new Set([...input.quantities, ...(moq ? [moq] : [])])].sort(
    (a, b) => a - b,
  )
  const engine = scaleScenarios({
    currency: input.currency,
    price: engineAmount(input.unitPrice),
    unitVariableCost: engineAmount(input.unitCost),
    initialSpend: { amount: initialSpend },
    moq,
    // 모르는 수수료는 계산 범위에서 뺀다 (sellingFeesIncluded = false 로 화면에 밝힌다)
    feeRate: feeKnown
      ? {
          amount: input.channelFeeRate.fraction,
          source: input.channelFeeRate.source as SourceType,
        }
      : { amount: 0 },
    quantities,
  })
  issues.push(...engine.issues)
  const outlaysComplete = !issues.some((issue) => issue.code === 'OUTLAY_MISSING')
  const rows = (engine.value?.rows ?? []).map((row) => {
    const revenue = fromExact(row.revenue)
    return {
      quantity: Number(row.quantity),
      producedQuantity: Number(row.producedQuantity),
      moqApplied: row.moqApplied,
      isMoq: Number(row.quantity) === moq,
      /** 초기 지출(아는 것)·MOQ 만큼 만든 비용을 이 판매 수량에 나눠 실은 낱개 원가 */
      costPerSoldUnit: row.costPerSoldUnit,
      /** 이 수량을 다 팔았을 때 남는 돈 (선택한 비용 범위, 고정비 제외) */
      contribution: row.contribution,
      contributionRatio:
        revenue.n === 0n ? null : toExact(div(fromExact(row.contribution), revenue)),
    }
  })
  return {
    status: issues.length ? ('incomplete' as const) : ('complete' as const),
    version: SCALE_LADDER_VERSION,
    issues,
    result: {
      currency: input.currency,
      sources: {
        unitCost: input.unitCost.source,
        unitPrice: input.unitPrice.source,
        channelFee: feeKnown ? input.channelFeeRate.source : ('unconfirmed' as const),
        moq: input.moq.source,
        outlays: input.outlays.map((line) => ({ id: line.id, source: line.amount.source })),
      },
      /** 실은 초기 지출 합계 (아는 것만). outlaysComplete 가 거짓이면 빠진 지출이 있다 */
      outlayTotal: initialSpend,
      outlaysComplete,
      moq,
      sellingFeesIncluded: feeKnown,
      /** 초기 지출을 되찾는 최소 판매 개수. 지출을 다 알고 낱개당 남는 돈이 있을 때만 */
      paybackUnits: outlaysComplete ? (engine.value?.paybackQuantity ?? null) : null,
      noPositiveContribution: engine.issues.some(
        (issue) => issue.code === 'NO_POSITIVE_UNIT_CONTRIBUTION',
      ),
      linearScaling: true as const,
      fixedCostsIncluded: false as const,
      netProfitClaim: false as const,
      rows,
    },
  }
}
export type ScaleLadderCalculation = ReturnType<typeof calculateScaleLadder>
